import { useRef, useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for animated nav-pills indicator.
 * Returns a ref to attach to the nav container div, and the indicator style (left, width).
 *
 * @param dependency - The state value(s) that determine which tab is active.
 *                     When this changes, the indicator position is recalculated.
 *
 * Usage:
 *   const { navRef, indicatorStyle } = useNavPillsIndicator(activeTab);
 *
 *   <div ref={navRef} className="nav-pills-animated">
 *     <div className="nav-pill-indicator" style={indicatorStyle} />
 *     <button className={`nav-link ${activeTab === 'a' ? 'active' : ''}`}>A</button>
 *     ...
 *   </div>
 */
export function useNavPillsIndicator(dependency: any) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  const [mountTick, setMountTick] = useState(0);

  const updateIndicator = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const activeBtn = container.querySelector('.nav-link.active') as HTMLElement | null;
    if (!activeBtn) return;
    const containerRect = container.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();
    setIndicator(prev => {
      const left = btnRect.left - containerRect.left;
      const width = btnRect.width;
      return (prev.left === left && prev.width === width) ? prev : { left, width };
    });
  }, []);

  const navRef = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node;
    if (node) setMountTick(n => n + 1);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    const timer = setTimeout(updateIndicator, 50);
    return () => clearTimeout(timer);
  }, [dependency, mountTick, updateIndicator]);

  const indicatorStyle: React.CSSProperties = {
    transform: `translateX(${indicator.left}px)`,
    width: indicator.width || 'auto',
  };

  return { navRef, indicatorStyle };
}
